import _ from "lodash";

import Vue from "vue";
import { mapGetters } from "vuex";
import { DnSelect } from "@/components/Elements";
import { LoadingSpinner } from "@/components";

import { stringHelper } from "@/components/mixins";

// Serices
import { ErrorLogService, 
  IAddPartListBuilderRequest, 
  ObjectUtil, 
  QuickOrderService, 
  PartListService,
  PartsService,
  PartDetailServices } from "@/services";


// Types
import { IQuickOrdersContext } from "../../QuickOrders.types";
import { ICustomerSkuDto } from "@/services/PartListService.ts"

export default Vue.extend({
  name: "EnterPartTab",
  mixins: [stringHelper],
  props: {
    context: {
      type: Object,
      default: () => ObjectUtil.createInstance<IQuickOrdersContext>(),
    },
    addPartToList: {
      type: Function,
      default: (params: IAddPartListBuilderRequest) => Promise.resolve(),
    },
  },
  components: {
    DnSelect,
    LoadingSpinner
  },
  computed: {
    ...mapGetters([
      "activeAccountId",
      "getContentItem",
      "user"
    ]),
    oemSelections() {
      if (this.oemListOptions.Items) {
        let sortedList = this.oemListOptions.Items.sort((a, b) =>
          (a.label > b.label) ? 1 : ((b.label > a.label) ? -1 : 0));
        return sortedList;
      } else {
        return [];
      }
    },
    isCustomerSku() {
      return this.user.IsCustomerSku;
    }
  },
  data: () => ({
    oemListOptions: [],
    oem: {
      code: "",
      label: "Optional",
    },
    partNumber: "",
    quantity: 1,
    location: "",
    dealerSku: "",
    isAdding: false,
    newCustomerSku: false,
    dealerSkuList: []
  }),
  beforeMount() {
    this.loadOemsAsync();
  },
  mounted(){
    this.focusPartNumberInput();
  },
  methods: {
    async loadOemsAsync(search?: string, loading?: (isLoading: boolean) => void) {
      try {
        if (loading) {
          loading(true);
        }

        const ctx = this.context as IQuickOrdersContext;

        const input = {
          accountId: ctx.accountId,
          userId: ctx.user.UserId,
          userAudiences: ctx.userAudiences,
          description: search,
          isOkToReverseCross: true,
          page: 1,
          pageSize: 10000,
        };

        this.oemListOptions = await QuickOrderService.getOemsList(input);
      } finally {
        if (loading) {
          loading(false);
        }
      }

      this.focusPartNumberInput();
    },

    searchOems: _.debounce(function(search: string, loading: boolean) {
      this.loadOemsAsync(search, loading);
    }, 2000),

    async submitForm() {
      if (!this.addPartInputsValid()) {
        return;
      }

      if (this.isAdding) {
        return;
      }

      try {
        this.isAdding = true;

        // save new customer sku if appropriate
        if (this.newCustomerSku) {
          await this.SaveNewCustomerSku();
        }

        const ctx = this.context as IQuickOrdersContext;

        let oemPartCode = "";
        let oemId = 0;
        let quantity = 1;
        let dealerPartNumber = "";
        let stockingLocation = "";

        if (this.partNumber) {
          oemPartCode = this.partNumber;
        }
        
        if (ctx.showDealerSku && this.dealerSku) {
          dealerPartNumber = this.dealerSku.replace(/[^a-zA-Z0-9 ]/g, '');
        }

        if (ctx.showLocation && this.location) {
          stockingLocation = this.location.replace(/[^a-zA-Z0-9 ]/g, '');
        }

        if (this.oem && this.oem.code !== "") {
          oemId = Number(this.oem.code);
        }

        if (this.quantity && !isNaN(this.quantity)) {
          quantity = Number(this.quantity);
        }

        const params = {
          accountId: ctx.accountId,
          userId: ctx.user.UserId,
          partListId: ctx.partListId,
          userAudiences: ctx.userAudiences,
          dealerPartNumber,
          oemId,
          oemPartCode,
          quantity,
          stockingLocation,
        };

        // Getting part data
        var partData = await this.getPartData(params);    
      
        //Checking to make sure the minimum order quantity is hit
        var meetsMinQty = await this.validateQuantity(params, partData.Id);

        if(meetsMinQty){
        
          //Otherwise add to the parts list
          await this.addPartToList(params);
          this.oem = {
            code: "",
            label: "Optional",
          };

          this.partNumber = "";
          this.quantity = null;
          this.location = "";
          this.dealerSku = ""; 
        }

      } catch (err) {
        ErrorLogService.LogAndGetErrorMessage(err);
      } finally {
        this.isAdding = false;
      }
      
    },
    submitOnFocus() {
      if (this.isAdding) {
        return;
      } else {
        this.submitForm();      
      }
    },
    focusPartNumberInput(){
      var partNumberInput = this.$refs?.partNumberInput?.$el ??  null;
      if(partNumberInput){
        this.$refs.partNumberInput.$el.focus();
      }   
    },

    async getPartData(params) {
      const parameters = {
          oemPartCode: params.oemPartCode,
          partId: null,
          accountId: params.accountId,
          userId: params.userId,
          audienceIds: params.audienceIds,
        };

      // Getting data for the part, in particular we need the part id
      const data = await PartDetailServices.getPartDetail(parameters);
      return data;
  },

    async validateQuantity(params, partId){
      // Getting full part details including the minimum order quantity
     var fullPartDetail = await PartsService.FullPartDetails(params.accountId, params.userId, partId, params.audienceIds)
        
         
     // Checking to make sure the minimumOrderQuantity is met
      if (fullPartDetail.data.MinimumOrderQuantity > params.quantity) {
        this.$store.commit("pushTimedMessage", {
          header: this.format(this.getContentItem("MinimumQuickOrderQuantity"), fullPartDetail.data.MinimumOrderQuantity),
          IsError: true,
          seconds: 5,
        });
        // If quantity does not meet the minimum requirement do not add to parts list
          return false;
      }
      else {
        return true;    
      }   
    },

    addPartInputsValid() {
      if (!this.partNumber || !this.quantity) {
        this.$store.commit("pushTimedMessage", {
          header: "Part Number and Quantity must have a value",
          IsError: true,
          seconds: 5
        });

        return false;
      } else {
        return true;
      }
    },
    async GetCustomerSkus(params: any) {
      await PartListService.GetCustomerSku(params).then((results) => {
        this.dealerSkuList = results;
        this.dealerSku = results.length > 0 ? results[0].CustomerSku : "";
      });
    },
    async SaveNewCustomerSku() {
      try {
        const params = {
          Id: null,
          AccountId: this.activeAccountId,
          OemPartCode: this.partNumber,
          CustomerSku: this.dealerSku.replace(/[^a-zA-Z0-9 ]/g, ''),
          IsPrimary: true
        } as ICustomerSkuDto;
        await PartListService.SaveCustomerSku(params);
      } catch (err) {
        ErrorLogService.LogException(err);
        
        this.$store.commit("pushTimedMessage", {
          header: "Failed to save new sku. Please try again, and if this continues please contact support.",
          IsError: true,
          seconds: 15
        });
      }
    }
  },
  watch: {
    partNumber: function(val, oldVal) {
      const params = {
        accountId: this.activeAccountId,
        oemPartCode: val
      }
      this.GetCustomerSkus(params);
    }
  }
});
